import { Award, CalendarDays, Eye, Flag, GraduationCap, Mail, MapPin, Phone, Target, Users } from 'lucide-react'
import { PageHero, SectionTitle } from '../components/Common'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { api } from '../services/api'
import type { PageContent, SchoolInfo } from '../types/content'

// Fallback school info
const defaultSchool: SchoolInfo = {
  name: 'TRƯỜNG THCS NGUYỄN VĂN LINH',
  slogan: 'Đoàn kết - Tự tin - Sáng tạo - Phát triển',
  address: '123 Đường Lê Lợi, P. Tân Thạnh, TP. Tam Kỳ, Quảng Nam',
  phone: '0235 3821 234',
  email: 'c2nguyenvanlinh@tamky.edu.vn',
  website: 'https://thcsnguyenvanlinh.tamky.edu.vn',
  founded: '05/09/2001',
  principal: 'Thầy Nguyễn Đức Minh',
}

const fileURL = (url?: string) => {
  if (!url) return ''
  if (/^https?:\/\//i.test(url)) return url
  return `${import.meta.env.VITE_CMS_URL || 'http://localhost:3001'}${url}`
}

const richTextParagraphs = (page?: PageContent) => {
  const children = (page?.content as { root?: { children?: Array<{ type?: string; children?: Array<{ text?: string }> }> } })?.root?.children
  const paragraphs = children
    ?.filter((node) => node.type === 'paragraph')
    .map((node) => node.children?.map((leaf) => leaf.text).filter(Boolean).join('') ?? '')
    .filter(Boolean)
  return paragraphs && paragraphs.length > 0 ? paragraphs : []
}

const hasManagedContent = (page?: PageContent) => richTextParagraphs(page).length > 0
const lines = (value?: string) => value?.split('\n').map((line) => line.trim()).filter(Boolean) ?? []
const aboutIcon = (icon?: string) => {
  const map: Record<string, typeof Award> = {
    flag: Flag,
    target: Target,
    award: Award,
    graduation: GraduationCap,
    users: Users,
    eye: Eye,
  }
  return icon ? (map[icon] || Award) : Award
}

export function AboutPage() {
  const { data } = useQuery({
    queryKey: ['site-config'],
    queryFn: () => api.getSiteConfig(),
  })
  const { data: aboutSettings } = useQuery({
    queryKey: ['about-page'],
    queryFn: () => api.getAboutPage(),
  })
  const { data: aboutPage } = useQuery({
    queryKey: ['page', 'gioi-thieu'],
    queryFn: () => api.getPage('gioi-thieu'),
  })
  const { data: historyPage } = useQuery({
    queryKey: ['page', 'gioi-thieu-lich-su'],
    queryFn: () => api.getPage('gioi-thieu-lich-su'),
  })
  const { data: visionPage } = useQuery({
    queryKey: ['page', 'gioi-thieu-tam-nhin-su-menh'],
    queryFn: () => api.getPage('gioi-thieu-tam-nhin-su-menh'),
  })
  const { data: leadershipPage } = useQuery({
    queryKey: ['page', 'gioi-thieu-ban-giam-hieu'],
    queryFn: () => api.getPage('gioi-thieu-ban-giam-hieu'),
  })
  const { data: achievementsPage } = useQuery({
    queryKey: ['page', 'gioi-thieu-thanh-tich'],
    queryFn: () => api.getPage('gioi-thieu-thanh-tich'),
  })

  const school = data?.site ?? defaultSchool
  const schoolName = school.schoolName ?? school.name ?? defaultSchool.name
  const schoolAddress = school.address ?? defaultSchool.address
  const schoolPhone = school.phone ?? defaultSchool.phone
  const schoolEmail = school.email ?? defaultSchool.email
  const schoolFounded = school.foundedAt ? new Date(school.foundedAt).toLocaleDateString('vi-VN') : (school.founded ?? defaultSchool.founded)
  const aboutParagraphs = richTextParagraphs(aboutPage)
  const introSettingParagraphs = lines(aboutSettings?.introDescription)
  const introParagraphs = introSettingParagraphs.length > 0
    ? introSettingParagraphs
    : aboutParagraphs.length > 0
      ? aboutParagraphs
      : [
        'Trường THCS Nguyễn Văn Linh, thành phố Tam Kỳ, tỉnh Quảng Nam là một trong những đơn vị giáo dục có bề dày truyền thống, không ngừng đổi mới và phát triển.',
        'Với đội ngũ cán bộ, giáo viên tâm huyết, học sinh chăm ngoan, cùng sự quan tâm của các cấp lãnh đạo và phụ huynh, nhà trường đã và đang khẳng định vị thế là địa chỉ tin cậy trong công tác dạy và học.',
      ]
  const introImage = fileURL(aboutSettings?.introImage?.url) || fileURL(aboutPage?.featuredImage?.url) || '/assets/images/school-building.jpg'
  const timelineItems = aboutSettings?.timelineItems?.filter((item) => item.title && item.year) ?? []
  const coreValues = aboutSettings?.coreValues?.filter((item) => item.title) ?? []
  const leaders = aboutSettings?.leaders?.filter((item) => item.fullName) ?? []
  const achievements = aboutSettings?.achievements?.filter((item) => item.value && item.label) ?? []

  return (
    <>
      <PageHero title="GIỚI THIỆU" />
      <div className="container about-page">
        <section className="about-intro">
          <img src={introImage} alt={aboutPage?.featuredImage?.alt || schoolName} />
          <div>
            <SectionTitle>{aboutSettings?.introTitle || aboutPage?.title || 'VỀ NHÀ TRƯỜNG'}</SectionTitle>
            {introParagraphs.slice(0, 3).map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
            <div className="about-contact-grid">
              <p><CalendarDays /><span><strong>Ngày thành lập</strong>{schoolFounded}</span></p>
              <p><Phone /><span><strong>Điện thoại</strong>{schoolPhone}</span></p>
              <p><MapPin /><span><strong>Địa chỉ</strong>{schoolAddress}</span></p>
              <p><Mail /><span><strong>Email</strong>{schoolEmail}</span></p>
            </div>
            <Link className="button-link" to="/gioi-thieu/ve-nha-truong">Xem chi tiết</Link>
          </div>
        </section>

        {timelineItems.length > 0 ? (
          <section className="timeline">
            <SectionTitle>{aboutSettings?.historyTitle || 'LỊCH SỬ HÌNH THÀNH VÀ PHÁT TRIỂN'}</SectionTitle>
            <div>
              {timelineItems.map((item) => {
                const Icon = aboutIcon(item.icon)
                return (
                  <article key={item.id || `${item.year}-${item.title}`}>
                    <span><Icon /></span>
                    <strong>{item.year}</strong>
                    <h3>{item.title}</h3>
                    {item.description && <p>{item.description}</p>}
                  </article>
                )
              })}
            </div>
          </section>
        ) : hasManagedContent(historyPage) ? (
          <ManagedAboutSection page={historyPage} fallbackTitle="LỊCH SỬ HÌNH THÀNH VÀ PHÁT TRIỂN" />
        ) : (
          <section className="timeline">
            <SectionTitle>LỊCH SỬ HÌNH THÀNH VÀ PHÁT TRIỂN</SectionTitle>
            <div>
              {[
                ['2001', 'Thành lập trường', 'Ngày 05/09/2001, Trường THCS Nguyễn Văn Linh chính thức được thành lập.', Flag],
                ['2005 - 2010', 'Ổn định và phát triển', 'Không ngừng nâng cao chất lượng giáo dục, cơ sở vật chất được đầu tư.', Target],
                ['2011 - 2016', 'Khẳng định vị thế', 'Đạt nhiều thành tích trong các kỳ thi học sinh giỏi cấp thành phố và cấp tỉnh.', Award],
                ['2017 - 2020', 'Đổi mới và hội nhập', 'Đẩy mạnh ứng dụng CNTT, đổi mới phương pháp dạy học, hội nhập giáo dục hiện đại.', GraduationCap],
                ['2021 - nay', 'Phát triển bền vững', 'Tiếp tục nâng cao chất lượng toàn diện, hướng tới trường đạt chuẩn quốc gia.', Flag],
              ].map(([year, title, desc, Icon]) => (
                <article key={String(year)}>
                  <span><Icon /></span>
                  <strong>{String(year)}</strong>
                  <h3>{String(title)}</h3>
                  <p>{String(desc)}</p>
                </article>
              ))}
            </div>
          </section>
        )}

        {coreValues.length > 0 ? (
          <section className="core-values">
            <SectionTitle>{aboutSettings?.valuesTitle || 'TẦM NHÌN - SỨ MỆNH - GIÁ TRỊ CỐT LÕI'}</SectionTitle>
            <div>
              {coreValues.map((item) => {
                const Icon = aboutIcon(item.icon)
                return <article key={item.id || item.title}><Icon /><h3>{item.title}</h3>{item.description && <p>{item.description}</p>}</article>
              })}
            </div>
          </section>
        ) : hasManagedContent(visionPage) ? (
          <ManagedAboutSection page={visionPage} fallbackTitle="TẦM NHÌN - SỨ MỆNH - GIÁ TRỊ CỐT LÕI" />
        ) : (
          <section className="core-values">
            <SectionTitle>TẦM NHÌN - SỨ MỆNH - GIÁ TRỊ CỐT LÕI</SectionTitle>
            <div>
              <article><Eye /><h3>TẦM NHÌN</h3><p>Trở thành ngôi trường chất lượng cao, môi trường giáo dục thân thiện, hiện đại.</p></article>
              <article><Target /><h3>SỨ MỆNH</h3><p>Cung cấp nền giáo dục toàn diện, trang bị kiến thức, kỹ năng và phẩm chất tốt đẹp.</p></article>
              <article><Award /><h3>GIÁ TRỊ CỐT LÕI</h3><p>Đoàn kết - Yêu thương - Tự tin - Trách nhiệm - Trung thực - Tôn trọng - Sáng tạo.</p></article>
            </div>
          </section>
        )}

        {leaders.length > 0 ? (
          <section className="leadership">
            <SectionTitle>{aboutSettings?.leadershipTitle || 'BAN GIÁM HIỆU'}</SectionTitle>
            <div>
              {leaders.map((person, index) => {
                const avatarUrl = fileURL(person.avatar?.url)
                const initial = person.fullName.split(' ').slice(-1)[0]?.[0] || '?'
                return (
                  <article key={person.id || person.fullName}>
                    {avatarUrl ? <img className="leader-photo" src={avatarUrl} alt={person.avatar?.alt || person.fullName} /> : <div className={`avatar avatar-${(index % 3) + 1}`}>{initial}</div>}
                    <div>
                      <h3>{person.fullName}</h3>
                      <strong>{person.position}</strong>
                      {person.degree && <p>Trình độ: {person.degree}</p>}
                      {person.phone && <p>Điện thoại: {person.phone}</p>}
                      {person.email && <p>Email: {person.email}</p>}
                    </div>
                  </article>
                )
              })}
            </div>
          </section>
        ) : hasManagedContent(leadershipPage) ? (
          <ManagedAboutSection page={leadershipPage} fallbackTitle="BAN GIÁM HIỆU" />
        ) : (
          <section className="leadership">
            <SectionTitle>BAN GIÁM HIỆU</SectionTitle>
            <div>
              {[
                ['Thầy Nguyễn Đức Minh', 'Hiệu trưởng', '0912 345 678'],
                ['Cô Trần Thị Thu Hương', 'Phó Hiệu trưởng', '0905 678 901'],
                ['Thầy Lê Văn Thành', 'Phó Hiệu trưởng', '0934 567 890']
              ].map((person, index) => (
                <article key={person[0]}>
                  <div className={`avatar avatar-${index + 1}`}>{person[0].split(' ').slice(-1)[0][0]}</div>
                  <div>
                    <h3>{person[0]}</h3>
                    <strong>{person[1]}</strong>
                    <p>Trình độ: Thạc sĩ Giáo dục học</p>
                    <p>Điện thoại: {person[2]}</p>
                    <p>Email: {person[0].toLowerCase().replaceAll(' ', '')}@tamky.edu.vn</p>
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}

        {achievements.length > 0 ? (
          <section className="achievement-cards">
            <SectionTitle>{aboutSettings?.achievementsTitle || 'THÀNH TÍCH NỔI BẬT'}</SectionTitle>
            <div>
              {achievements.map((item) => {
                const Icon = aboutIcon(item.icon)
                return <article key={item.id || `${item.value}-${item.label}`}><Icon /><strong>{item.value}</strong><span>{item.label}</span></article>
              })}
            </div>
          </section>
        ) : hasManagedContent(achievementsPage) ? (
          <ManagedAboutSection page={achievementsPage} fallbackTitle="THÀNH TÍCH NỔI BẬT" />
        ) : (
          <section className="achievement-cards">
            <SectionTitle>THÀNH TÍCH NỔI BẬT</SectionTitle>
            <div>
              <article><Award /><strong>25+</strong><span>Năm xây dựng và phát triển</span></article>
              <article><GraduationCap /><strong>1200+</strong><span>Học sinh đang theo học</span></article>
              <article><TrophyIcon /><strong>150+</strong><span>Giải học sinh giỏi các cấp</span></article>
              <article><FileCertificate /><strong>5</strong><span>Bằng khen của UBND tỉnh</span></article>
              <article><Users /><strong>Chuẩn Quốc gia</strong><span>Mức độ 2</span></article>
            </div>
          </section>
        )}
      </div>
    </>
  )
}

function ManagedAboutSection({ page, fallbackTitle }: { page?: PageContent; fallbackTitle: string }) {
  return (
    <section className="managed-about-section">
      <SectionTitle>{page?.title || fallbackTitle}</SectionTitle>
      {page?.summary && <p className="managed-about-section__summary">{page.summary}</p>}
      <SimpleRichText content={page?.content} />
    </section>
  )
}

function SimpleRichText({ content }: { content: unknown }) {
  const children = (content as { root?: { children?: unknown[] } })?.root?.children
  if (!children) return null
  return <div className="rich-text">{children.map((node: any, index) => renderRichNode(node, index))}</div>
}

function renderRichNode(node: any, key: number) {
  if (!node) return null
  if (node.type === 'paragraph') return <p key={key}>{node.children?.map((child: any, index: number) => renderRichLeaf(child, index))}</p>
  if (node.type === 'heading') {
    const Tag = `h${node.tag?.replace('h', '') || '3'}` as 'h2' | 'h3' | 'h4'
    return <Tag key={key}>{node.children?.map((child: any, index: number) => renderRichLeaf(child, index))}</Tag>
  }
  if (node.type === 'list') {
    return node.listType === 'number'
      ? <ol key={key}>{node.children?.map((child: any, index: number) => renderRichNode(child, index))}</ol>
      : <ul key={key}>{node.children?.map((child: any, index: number) => renderRichNode(child, index))}</ul>
  }
  if (node.type === 'listitem') return <li key={key}>{node.children?.map((child: any, index: number) => renderRichLeaf(child, index))}</li>
  return null
}

function renderRichLeaf(leaf: any, key: number) {
  if (!leaf || leaf.type !== 'text') return null
  let el: any = leaf.text
  if (leaf.format & 1) el = <strong>{el}</strong>
  if (leaf.format & 2) el = <em>{el}</em>
  if (leaf.format & 8) el = <u>{el}</u>
  return <span key={key}>{el}</span>
}

function TrophyIcon() { return <Award /> }
function FileCertificate() { return <Award /> }

export function SchoolDetailPage() {
  const { data } = useQuery({
    queryKey: ['site-config'],
    queryFn: () => api.getSiteConfig(),
  })
  const { data: aboutSettings } = useQuery({
    queryKey: ['about-page'],
    queryFn: () => api.getAboutPage(),
  })
  const { data: detailPage } = useQuery({
    queryKey: ['page', 've-nha-truong'],
    queryFn: () => api.getPage('ve-nha-truong'),
  })

  const school = data?.site ?? defaultSchool
  const schoolName = school.schoolName ?? school.name ?? defaultSchool.name
  const schoolAddress = school.address ?? defaultSchool.address
  const schoolPhone = school.phone ?? defaultSchool.phone
  const schoolEmail = school.email ?? defaultSchool.email
  const schoolWebsite = school.website ?? defaultSchool.website
  const schoolFounded = school.foundedAt ? new Date(school.foundedAt).toLocaleDateString('vi-VN') : (school.founded ?? defaultSchool.founded)
  const schoolPrincipal = school.principalName ?? school.principal ?? defaultSchool.principal
  const detailParagraphs = richTextParagraphs(detailPage)
  const detailSettingParagraphs = lines(aboutSettings?.detailDescription)
  const overviewParagraphs = detailSettingParagraphs.length > 0
    ? detailSettingParagraphs
    : detailParagraphs.length > 0
      ? detailParagraphs
      : [
        'Trường THCS Nguyễn Văn Linh được thành lập ngày 05/09/2001, là một trong những đơn vị giáo dục có bề dày truyền thống của thành phố Tam Kỳ, tỉnh Quảng Nam.',
        'Nhà trường hiện có đội ngũ cán bộ, giáo viên tận tâm, giàu chuyên môn, luôn đổi mới phương pháp dạy học, chú trọng giáo dục toàn diện.',
      ]
  const detailImage = fileURL(aboutSettings?.detailImage?.url) || fileURL(detailPage?.featuredImage?.url) || '/assets/images/school-building.jpg'

  const facilities = aboutSettings?.facilities?.filter((item) => item.title) ?? []
  const fallbackFacilities = [
    { title: 'Phòng học khang trang, hiện đại', image: { url: '/assets/images/training-subjects.jpg' } },
    { title: 'Phòng tin học', image: { url: '/assets/images/training-program.jpg' } },
    { title: 'Phòng thực hành', image: { url: '/assets/images/training-stem.jpg' } },
    { title: 'Thư viện', image: { url: '/assets/images/activity-reading.jpg' } },
    { title: 'Sân thể thao', image: { url: '/assets/images/activity-football.jpg' } },
  ]
  const visibleFacilities = facilities.length > 0 ? facilities : fallbackFacilities
  const developmentParagraphs = lines(aboutSettings?.developmentDescription)
  const developmentBullets = aboutSettings?.developmentBullets?.filter((item) => item.text).map((item) => item.text) ?? []
  const developmentImage = fileURL(aboutSettings?.developmentImage?.url) || '/assets/images/hero-school.jpg'

  return (
    <>
      <PageHero title="GIỚI THIỆU VỀ NHÀ TRƯỜNG" trail="Giới thiệu / Về nhà trường" />
      <div className="container school-detail">
        <SectionTitle>1. THÔNG TIN CHUNG</SectionTitle>
        <div className="school-info">
          <img src={detailImage} alt={detailPage?.featuredImage?.alt || 'Nhà trường'} />
          <dl>
            {[
              ['Tên trường', schoolName],
              ['Địa chỉ', schoolAddress],
              ['Điện thoại', schoolPhone],
              ['Email', schoolEmail],
              ['Website', schoolWebsite],
              ['Ngày thành lập', schoolFounded],
              ['Hiệu trưởng', schoolPrincipal],
              ['Loại hình', 'Công lập'],
              ['Cấp học', 'Trung học cơ sở'],
              ['Mã trường', school.schoolCode || '40050705']
            ].map(([key, value]) => (
              <div key={key}>
                <dt>{key}:</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
        </div>

        <section className="detail-overview">
          <div>
            <SectionTitle>2. {aboutSettings?.detailTitle || detailPage?.title || 'GIỚI THIỆU CHUNG'}</SectionTitle>
            {overviewParagraphs.slice(0, 5).map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
          </div>
          <div className="summary-stats">
            <article><Award /><strong>20+</strong><span>Năm hình thành và phát triển</span></article>
            <article><Users /><strong>45+</strong><span>Cán bộ, giáo viên</span></article>
            <article><GraduationCap /><strong>1.200+</strong><span>Học sinh toàn trường</span></article>
            <article><BookIcon /><strong>25+</strong><span>Lớp học</span></article>
          </div>
        </section>

        <section>
          <SectionTitle>3. CƠ SỞ VẬT CHẤT</SectionTitle>
          <div className="facility-grid">
            {visibleFacilities.map(({ title, image }) => (
              <article key={title}>
                <img src={fileURL(image?.url) || '/assets/images/school-building.jpg'} alt={(image && 'alt' in image ? image.alt : undefined) || title} />
                <strong>{title}</strong>
              </article>
            ))}
          </div>
        </section>

        <section>
          <SectionTitle>4. THÀNH TÍCH NỔI BẬT</SectionTitle>
          <div className="achievement-boxes">
            <article><Award />
              <h3>Dạy và học</h3>
              <ul>
                <li>Nhiều năm liền đạt danh hiệu Tập thể Lao động tiên tiến.</li>
                <li>Tỷ lệ học sinh khá, giỏi luôn nằm ở tốp đầu thành phố.</li>
              </ul>
            </article>
            <article><GraduationCap />
              <h3>Học sinh</h3>
              <ul>
                <li>Đạt nhiều giải cao trong các kỳ thi học sinh giỏi.</li>
                <li>Nhiều học sinh đoạt giải khoa học kỹ thuật, văn hóa, thể thao.</li>
              </ul>
            </article>
            <article><Users />
              <h3>Hoạt động khác</h3>
              <ul>
                <li>Công tác Đoàn - Đội vững mạnh nhiều năm.</li>
                <li>Các phong trào thi đua được tổ chức sôi nổi, hiệu quả.</li>
              </ul>
            </article>
          </div>
        </section>

        <section className="development">
          <div>
            <SectionTitle>5. {aboutSettings?.developmentTitle || 'ĐỊNH HƯỚNG PHÁT TRIỂN'}</SectionTitle>
            {(developmentParagraphs.length > 0 ? developmentParagraphs : ['Nhà trường tiếp tục phấn đấu xây dựng môi trường giáo dục thân thiện, kỷ cương, hiện đại; nâng cao chất lượng giáo dục toàn diện; đổi mới sáng tạo trong dạy và học.']).map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
            <ul>
              {(developmentBullets.length > 0 ? developmentBullets : [
                'Nâng cao chất lượng dạy và học',
                'Ứng dụng công nghệ thông tin trong quản lý và giảng dạy',
                'Xây dựng trường học xanh - sạch - đẹp - an toàn',
                'Phát triển giáo dục toàn diện, chú trọng kỹ năng và đạo đức',
              ]).map((item) => <li key={item}>{item}</li>)}
            </ul>
          </div>
          <img src={developmentImage} alt={aboutSettings?.developmentImage?.alt || 'Cổng trường'} />
        </section>
      </div>
    </>
  )
}

function BookIcon() { return <GraduationCap /> }
